'use client'

import { Field, Radio, RadioGroup } from '@headlessui/react'
import clsx from 'clsx'
import type { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import { useCallback, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import InputError from '../InputError/InputError'

interface RadioInputOption {
  /**
   * Value of the radio input
   */
  value: number | string
  /**
   * Label of the radio input
   */
  label: string
  /**
   * Image source to use instead of label if defined
   */
  image?: string | { src: string }
  /**
   * Whether the radio input is disabled
   */
  disabled?: boolean
}

interface RadioInputStandaloneProps
  extends Omit<FormFieldCommonProps, 'onChange' | 'defaultValue'> {
  /**
   * Radio input options
   */
  options: RadioInputOption[]
  /**
   * Display radio inputs inline (horizontally)
   */
  displayOptionsInline?: boolean
  /**
   * Default value of the radio input
   */
  defaultValue?: RadioInputOption['value']
  /**
   * Optional override className for the label
   */
  labelClassName?: string
  /**
   * Optional override className for the radio group
   */
  radioGroupClassName?: string
  /**
   * Callback when the value changes
   */
  onChange?: (data: string | number) => void
}

export interface RadioInputProps
  extends RadioInputStandaloneProps,
    Omit<ControlledFormField, 'onChange' | 'defaultValue'> {}

// Standalone component that can be used outside react-hook-form's context
export function RadioInputStandalone({
  name,
  wrapperClassName,
  radioGroupClassName,
  className,
  error,
  options,
  defaultValue,
  displayOptionsInline = false,
  labelClassName,
  onChange,
}: RadioInputStandaloneProps) {
  const [defaultValueCached, setDefaultValueCached] = useState(defaultValue)
  const [selected, setSelected] = useState(defaultValue)

  const onSelect = useCallback(
    (data: string) => {
      setSelected(data)
      onChange?.(data)
    },
    [onChange],
  )

  useEffect(() => {
    if (defaultValue !== defaultValueCached) {
      setDefaultValueCached(defaultValue)
      setSelected(defaultValue)
    }
  }, [defaultValue, defaultValueCached, onSelect])

  return (
    <div className={clsx(`radio-input`, wrapperClassName)}>
      <RadioGroup
        className={clsx(
          displayOptionsInline
            ? 'flex gap-x-4 md:gap-x-8'
            : 'flex flex-col items-start gap-y-6',
          radioGroupClassName,
        )}
        data-rd
        value={selected}
        onChange={onSelect}
        name={name}
      >
        {options.map((option) => (
          <Field key={option.value}>
            <Radio
              value={option.value}
              disabled={option.disabled}
              className={clsx(
                'group flex items-center relative cursor-pointer',
                // Focus outline
                'after:absolute after:-top-2 after:-bottom-2 after:-left-2 after:-right-2 after:pointer-events-none after:rounded-8',
                'focus-visible:outline-none focus-visible:after:shadow-input-focus data-[focus]:after:shadow-input-focus-accent-link',
                // Disabled state
                'data-[disabled]:text-neutral-600 data-[disabled]:cursor-not-allowed',
              )}
            >
              <span
                className={clsx(
                  className,
                  'inline-flex items-center justify-center w-5 h-5 mr-2 relative rounded-full',
                  // Radio Border
                  'before:w-full before:h-full before:absolute before:border before:border-current before:rounded-full before:transition-[border]',
                  // Radio disc
                  'after:w-[10px] after:h-[10px] after:bg-current after:rounded-full after:hidden',
                  // Checked state
                  'group-data-[checked]:after:block',
                  // Hover and pressed states
                  'group-data-[hover]:before:border-2',
                  { 'text-error': error },
                )}
              ></span>
              {option.image ? (
                <img
                  src={
                    typeof option.image === 'string'
                      ? option.image
                      : option.image.src
                  }
                  alt={option.label}
                  className="h-6 w-auto"
                />
              ) : (
                <span
                  className={clsx(
                    labelClassName ? labelClassName : 'type-body-7',
                  )}
                >
                  {option.label}
                </span>
              )}
            </Radio>
          </Field>
        ))}
      </RadioGroup>
      <InputError>{error}</InputError>
    </div>
  )
}

// Controlled form element that is automatically registered to react-hook-form
export default function RadioInput({
  name,
  control,
  rules,
  onChange,
  defaultValue,
  shouldUnregister,
  ...props
}: RadioInputProps) {
  return (
    <Controller
      name={name || ''}
      control={control}
      rules={rules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      render={({ field: { name, onChange: fieldOnChange }, fieldState }) => {
        return (
          <RadioInputStandalone
            name={name}
            defaultValue={defaultValue}
            onChange={(data) => {
              fieldOnChange?.(data)
              onChange?.(data)
            }}
            {...props}
            error={fieldState.error?.message}
          />
        )
      }}
    />
  )
}
